import api from './api';
import { getApiBaseUrl } from '../utils/domainUtils';

export const authService = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  isAdmin: () => {
    const user = authService.getCurrentUser();
    return user?.role === 'admin';
  },

  getToken: () => {
    return localStorage.getItem('token');
  },

  // Get current user info from server
  fetchCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/me');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    } catch (error) {
      console.error('Fetch current user error:', error);
      authService.logout();
      throw error;
    }
  },

  // Update current user's language preference
  updateLanguage: async (language) => {
    try {
      const response = await api.put('/api/users/me/language', { language });
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Update language error:', error);
      throw error;
    }
  },

  // Complete TOTP login
  loginWithTOTP: async (tempToken, totpToken) => {
    const response = await api.post('/api/auth/login/totp', { 
      tempToken, 
      totpToken 
    });
    return response.data;
  },

  // Request password reset
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token, password) => {
    const response = await api.post('/api/auth/reset-password', { token, password });
    return response.data;
  },

  // Verify reset token
  verifyResetToken: async (token) => {
    const response = await api.get(`/api/auth/verify-reset-token/${token}`);
    return response.data;
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/api/auth/me/profile', profileData);
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  // Upload avatar
  uploadAvatar: async (file, cropData) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      if (cropData) {
        formData.append('cropData', JSON.stringify(cropData));
      }

      const response = await api.post('/api/auth/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update local user data with new avatar
      const user = authService.getCurrentUser();
      if (user) {
        user.profile_picture = response.data.profile_picture;
        localStorage.setItem('user', JSON.stringify(user));
      }

      return response.data;
    } catch (error) {
      console.error('Upload avatar error:', error);
      throw error;
    }
  },

  // Delete avatar
  deleteAvatar: async () => {
    try {
      const response = await api.delete('/api/auth/me/avatar');
      
      // Update local user data
      const user = authService.getCurrentUser();
      if (user) {
        user.profile_picture = null;
        localStorage.setItem('user', JSON.stringify(user));
      }

      return response.data;
    } catch (error) {
      console.error('Delete avatar error:', error);
      throw error;
    }
  },

  // Get avatar URL using dynamic domain detection
  getAvatarUrl: (userId, thumbnail = false) => {
    const user = authService.getCurrentUser();
    if (!user || !user.profile_picture) return null;
    
    // Use dynamic API base URL detection
    const baseUrl = getApiBaseUrl();
    const filename = thumbnail ? `thumb_${user.profile_picture}` : user.profile_picture;
    
    // If baseUrl is empty (relative URLs), construct relative path
    if (baseUrl === '') {
      return `/uploads/avatars/${filename}`;
    }
    
    return `${baseUrl}/uploads/avatars/${filename}`;
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/auth/me/password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }
};

